// BLE authentication — the §3 handshake from the firmware's BLE_MOBILE spec.
//
// A device rejects every command until this succeeds. Only AUTH and PING are
// accepted before it, so getting this wrong does not degrade the feature — it
// removes it entirely.
//
// ── The maths ──────────────────────────────────────────────────────────────
//   device_key = HMAC_SHA256(family_secret, <UniqueNumber ASCII>)   32 bytes
//   response   = HMAC_SHA256(device_key, <16 nonce bytes>)[0..16]   hex, upper
//
// ⚠️ THE NONCE IS HEX-DECODED FIRST. Hashing the hex STRING instead of the 16
// decoded bytes is, per the spec, the mistake everyone makes: it produces a
// plausible 32-char answer and a flat AUTH FAIL. selfTest() below exists so that
// error can never ship silently — it is checked against the spec's own vectors.
//
// ── Why @noble/hashes and not crypto.subtle ────────────────────────────────
// crypto.subtle only exists in a SECURE CONTEXT. Capacitor serves this app from
// http://localhost, which browsers do treat as trustworthy — but "should be
// fine" is a poor bet for the one code path that gates all BLE control, and the
// same assumption already proved false for navigator.clipboard elsewhere in this
// app. @noble is a small audited pure-JS implementation with no such condition,
// and it is what the spec itself recommends.

import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';

// ── Family secrets, by key version ─────────────────────────────────────────
//
// ⚠️ VERSION 1 IS THE DEVELOPMENT KEY AND MUST NOT SHIP. The spec is explicit:
// "Smartify-1137" has been written into shared documents, is short and
// guessable, and production needs its own secret generated locally
// (`openssl rand -hex 32`) that appears in no document, chat or ticket.
//
// The table exists so that replacing it is a release rather than a hardware
// recall: new devices ship on v2 while the app still answers v1 for everything
// already installed. It cannot be retrofitted later, which is why it is here
// before there is a second entry.
//
// ⚠️ AND IN THIS APP THE SECRET IS SOFTER THAN THE SPEC ASSUMES. It says the key
// "can be recovered by decompiling" the binary. Here it sits in a JavaScript
// bundle that OTA serves over plain HTTP, so it is readable in transit without
// touching a phone at all. Treat BLE as adequate for lighting, never as a trust
// boundary — which is the spec's own conclusion, only more so.
export const FAMILY_SECRETS = {
  1: 'Smartify-1137',
};

// Which version to use when the device has not told us otherwise.
//
// The spec says the device "stores a key_version byte and advertises it", but
// the advertised service data it specifies is <state:1><owner_tag:4> — five
// bytes with no version field. There is nowhere to read it from, so this is
// pinned until the firmware side resolves that. Raised with them; see
// bleConfig.parseServiceData.
export const DEFAULT_KEY_VERSION = 1;

const enc = (s) => new TextEncoder().encode(s);

export const toHex = (bytes) => Array.from(bytes)
  .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
  .join('');

export function fromHex(hex) {
  const clean = String(hex || '').trim();
  const pairs = clean.match(/../g) || [];
  return Uint8Array.from(pairs.map((b) => parseInt(b, 16)));
}

/** 32 raw bytes, unique per device. Keep it binary — never hex it before use. */
export function deviceKey(uniqueNumber, keyVersion = DEFAULT_KEY_VERSION) {
  const secret = FAMILY_SECRETS[keyVersion];
  if (!secret) throw new Error(`No family secret for key version ${keyVersion}`);
  // Uppercase, no separators — the exact 12 ASCII bytes the firmware hashes.
  const un = String(uniqueNumber || '').toUpperCase().replace(/[^0-9A-F]/g, '');
  return hmac(sha256, enc(secret), enc(un));
}

/** The 32-char answer to a nonce, given any key (device key or owner secret). */
export function responseFor(key, nonceHex) {
  return toHex(hmac(sha256, key, fromHex(nonceHex)).slice(0, 16));
}

export function authResponse(uniqueNumber, nonceHex, keyVersion = DEFAULT_KEY_VERSION) {
  return responseFor(deviceKey(uniqueNumber, keyVersion), nonceHex);
}

/**
 * Stage 2, for an assigned device. Keyed with the owner_secret the backend
 * issued at claim time, over the SAME nonce as stage 1.
 *
 * Accepts the secret as hex (how a backend would return 32 random bytes) or as
 * raw bytes.
 */
export function ownerResponse(ownerSecret, nonceHex) {
  const key = ownerSecret instanceof Uint8Array ? ownerSecret : fromHex(ownerSecret);
  return responseFor(key, nonceHex);
}

/**
 * The 4-byte tag a device advertises once assigned: SHA256(owner_id)[0..4].
 *
 * Lets the scan list separate "mine" from "someone else's" without connecting.
 * Spec worked example: owner_id "12345" -> 5994471A.
 */
export function ownerTag(ownerId) {
  return toHex(sha256(enc(String(ownerId ?? ''))).slice(0, 4));
}

// ── Notification parsing ───────────────────────────────────────────────────
// "NONCE:<32 hex>,<UNASSIGNED|ASSIGNED>"
export function parseNonce(text) {
  const m = /^NONCE:([0-9A-Fa-f]{32})(?:,\s*(\w+))?/.exec(String(text || '').trim());
  if (!m) return null;
  return { nonceHex: m[1].toUpperCase(), assigned: String(m[2] || '').toUpperCase() === 'ASSIGNED' };
}

const upper = (t) => String(t || '').trim().toUpperCase();

export const isAuthOk = (t) => upper(t) === 'AUTH OK';
export const isAuthFail = (t) => upper(t) === 'AUTH FAIL';
export const isOwnerRequired = (t) => upper(t) === 'OWNER REQUIRED';

// ── Self-test ──────────────────────────────────────────────────────────────
// The spec's own vectors (§3). "If your implementation reproduces both, the auth
// code is correct." Cheap enough to run at startup, and it turns a silent
// AUTH FAIL on hardware into a clear failure here.
export const AUTH_VECTORS = [
  {
    uniqueNumber: '083A8D406BE4',
    deviceKey: '95117C8944371655AAE166334AD14FE07ACF5C85320F8148B6C2A6B0450BBE19',
    nonce: 'A3F19C4B7E2D8615F0C93A5D1B8E7240',
    response: '38C0296047C8FEADF3EFB20BA8C6EFA3',
  },
  {
    uniqueNumber: 'A1B2C3D4E5F6',
    deviceKey: '4A51AEA620BFACA1CFE63FE4B1BFFD95BFA4D59186915AE8728FB77CF3564FA2',
    nonce: '00112233445566778899AABBCCDDEEFF',
    response: 'D1B4175059CA0CAAB4D792C0B4A97545',
  },
];

/** true when both spec vectors reproduce exactly. */
export function selfTest() {
  return AUTH_VECTORS.every((v) => {
    try {
      const k = deviceKey(v.uniqueNumber, 1);
      return toHex(k) === v.deviceKey && responseFor(k, v.nonce) === v.response;
    } catch (e) {
      return false;
    }
  });
}
